/**
 * Every word on the sign-in page, in both editions.
 *
 * The page's language is not a build-time choice and not the browser's: it is the TENANT's, set in
 * the brand console and served to this page by `GET /v1/config` (see `api/brand.py`). One build
 * therefore signs both tenants in, in whichever language each of them publishes.
 *
 * `Copy` is a closed interface and `COPY` is a `Record<Language, Copy>`, so TypeScript refuses a
 * locale that is missing a key. That is deliberate: a half-translated sign-in page — English
 * buttons under a Spanish heading — is worse than an English one, and nothing here would catch it
 * at runtime.
 *
 * The Spanish is Rioplatense, voseo, matching the tenants' published copy: `ingresá`, not `ingresa`.
 */

/** The languages the fleet publishes in. Mirrors `brand`'s own `LANGUAGES`. */
export const LANGUAGES = ['en', 'es'] as const;

export type Language = (typeof LANGUAGES)[number];

/** What the page speaks until the API answers, and if it never does. */
export const DEFAULT_LANGUAGE: Language = 'en';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

export interface Copy {
  /** The browser tab. `{domain}` is replaced with the tenant's apex. */
  readonly documentTitle: string;
  /** The display heading over the epigraph, on the picture half. */
  readonly welcome: string;
  /** The epigraph, two statements set against each other — see the markup for why they are two. */
  readonly epigraphFirst: string;
  readonly epigraphSecond: string;
  /** The form half's heading and its one line of explanation. */
  readonly signIn: string;
  readonly signInSubtitle: string;
  /** The provider buttons: the network's name and nothing else (m7 docs/STYLE.md, one word). */
  readonly withGoogle: string;
  readonly withLinkedIn: string;
  readonly withFacebook: string;
  readonly withX: string;
  /** The legal line under the buttons, and its two links. */
  readonly legalPrefix: string;
  readonly termsOfService: string;
  readonly legalJoin: string;
  readonly privacyPolicy: string;
  /** Said, beside a turning ring, from the provider's answer until the next app has opened. */
  readonly verifying: string;
  /** What the page says when a sign-in does not happen. */
  readonly cancelled: string;
  /** `{error}` is the provider's own error code. */
  readonly providerError: string;
  readonly invalidState: string;
  readonly incomplete: string;
  readonly unavailable: string;
  /** `{code}` is the HTTP status. */
  readonly failedWithCode: string;
  readonly unreachable: string;
  readonly failed: string;
}

const EN: Copy = {
  documentTitle: 'Login — {domain}',
  welcome: 'Welcome',
  epigraphFirst: 'Chaos is the law of nature',
  epigraphSecond: 'Order is the dream of man',
  signIn: 'Sign in',
  signInSubtitle: 'Choose how to sign in',
  withGoogle: 'Google',
  withLinkedIn: 'LinkedIn',
  withFacebook: 'Facebook',
  withX: 'X',
  legalPrefix: 'By signing in, you agree to our',
  termsOfService: 'Terms of Service',
  legalJoin: 'and',
  privacyPolicy: 'Privacy Policy',
  verifying: 'Signing you in…',
  cancelled: 'Sign-in was cancelled',
  providerError: 'Sign-in error: {error}',
  invalidState: 'Invalid sign-in state. Please try again.',
  incomplete: 'Sign-in could not be completed. Please try again.',
  unavailable: 'Sign-in service is temporarily unavailable. Please try again later.',
  failedWithCode: 'Sign-in failed ({code})',
  unreachable:
    'Could not reach the sign-in service. If this persists, the API may be down or blocked (check network and CORS).',
  failed: 'Login failed',
};

const ES: Copy = {
  documentTitle: 'Ingresar — {domain}',
  welcome: 'Bienvenido',
  epigraphFirst: 'El caos es la ley de la naturaleza',
  epigraphSecond: 'El orden es el sueño del hombre',
  signIn: 'Ingresá',
  signInSubtitle: 'Elegí cómo ingresar',
  withGoogle: 'Google',
  withLinkedIn: 'LinkedIn',
  withFacebook: 'Facebook',
  withX: 'X',
  legalPrefix: 'Al ingresar, aceptás nuestros',
  termsOfService: 'Términos del Servicio',
  legalJoin: 'y la',
  privacyPolicy: 'Política de Privacidad',
  verifying: 'Iniciando sesión…',
  cancelled: 'Se canceló el ingreso',
  providerError: 'Error al ingresar: {error}',
  invalidState: 'El estado del ingreso no es válido. Probá de nuevo.',
  incomplete: 'No se pudo completar el ingreso. Probá de nuevo.',
  unavailable: 'El servicio de ingreso no está disponible por el momento. Probá más tarde.',
  failedWithCode: 'Falló el ingreso ({code})',
  unreachable:
    'No se pudo contactar el servicio de ingreso. Si sigue pasando, puede que la API esté caída o bloqueada (mirá la red y CORS).',
  failed: 'Falló el ingreso',
};

export const COPY: Record<Language, Copy> = { en: EN, es: ES };

/** One placeholder, substituted everywhere it appears. `format(copy.failedWithCode, {code: 502})`. */
export function format(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
    template,
  );
}
